/* =====================================================================
   B1 — THE CLIP IS THE WAY HOME
   (R14's file: it was the drawing pin's; R23 gave the way home to the
   sheet's own north-east corner; the corner has now given it back)

   Tommy, 2026-09-19: "It's not a pin… a raised bit of paper in the
   corner… let's stop calling it a pin… something like a clipboard-style
   clip. The map starts clipped, and as soon as you pan it, it goes
   unclipped. You can clip it back down… it's the map that you're
   clipping."

   So there is a spring clip at the HEAD of the sheet — over the top edge,
   a little left of centre, well clear of the north-east corner, which is
   the fold's and only the fold's now. It is a thing, not an icon: a
   barrel, a jaw, a pressure lip, painted metal, and its own shadow on the
   paper. It starts CLOSED, holding the map flat. The first pan or zoom
   off home springs it OPEN and it stays open for as long as the view is
   off home. Pressing it shut is `Room.home(true)` — the map is pressed
   back flat and home in one gesture, because it is the map being clipped.

   THE CORNER NO LONGER GOES HOME. Its push is gone from the release
   below; what is left there is the PULL, which is the fold, and the lift
   and curl objects.js gives it are that gesture's only affordance.
   (R23's original head follows, for what stayed and why.)

   R23 — THE SHEET'S OWN CORNER IS THE WAY HOME

   Tommy, given the choice: "the sheet's own corner is decent. how would we
   indicate it's clickable on hover?"

   WHAT WENT. R14 laid v32's red drawing pin in the north-east margin as a
   small SVG on the paper — a head that inked itself the moment there was
   something to undo, a 2.5 px lift under the pointer, inert at the fit.
   It worked and it was still a BUTTON: a drawn symbol of an action, the
   one piece of chrome on a sheet that is otherwise all paper, answering
   "can I press this" the way a toolbar does. The pin's element, its five
   inks, its two states and its grip are gone from base.njk and from the
   stylesheet, and the printed one stays stripped out of the furniture
   raster (engine.js furniture(), now unconditionally) — there is no pin in
   this engine, drawn or rastered.

   WHAT STAYED. Everything that was actually the WAY home rather than the
   picture of it: `Room.atFit`, `Room.home`, the double-click on the desk
   (and not on the plate), R and 0, and Esc's own chain in engine.js. The
   year index's marked cell is R21's and was never in this file.

   WHAT IS HERE INSTEAD. The paper answers for itself: objects.js lifts and
   curls the sheet's north-east corner whenever the view is off the fit
   (RoomObjects' `uNE`), and this file is the HAND — one empty, undrawn div
   laid over that corner by E9's projection, exactly as the pin was laid:

     · it is laid in SHEET units, not the over layer's, because the corner
       is the sheet's own geometry and not a thing printed on it — one css
       pixel of the element is one sheet unit, so `Room.onPaper(x, y, 1)`
       is the whole of its placement and it rides every tilt, pan, bearing
       and room pose with no draw and no rebuild;
     · it is INERT AT THE FIT, like the pin: no cursor, no pointer events,
       nothing to hover, because there is nothing to undo;
     · hovering it does not change the div — it asks the PAPER to lift two
       millimetres more and deepen its own shadow. The affordance is the
       thing itself moving, which is v11's ruling ("let the UI just be
       discovered by the movement on hover") taken to its end: there is no
       longer any UI to discover, only paper that has not lain down;
     · a push is `Room.home(true)` and `RoomObjects.cornerSettle()`, so the
       corner lies down over the same 620 ms the sheet takes to lay itself
       back out — it settles AS the sheet eases, not before it.

   ===================================================================== */
(function () {
'use strict';

/* ==== X2: AND THE SAME CORNER FOLDS THE SHEET ========================

   Tommy (IDEAS #23.3): "the sheet lifting the corner, folding it up could
   be the route out … we've already got the corner kind of resetting the
   map view, so maybe we'd need to rethink that … something a bit
   cleverer."

   The two gestures are already different and always were. R23's way home
   is A PUSH: a press that does not travel. `moved > 4` has aborted it
   since R14, so a hand that takes hold of the corner and PULLS has, until
   now, done nothing at all. That is where the fold goes, and it needs
   nothing rethought: push the corner and the sheet lies back out; pull it
   across the sheet and the sheet folds. Push is a fingertip, pull is a
   hand — which is exactly the difference between flattening a map and
   folding one.

   HOW FAR IS HOW FAR. The progress is the hand's travel PROJECTED ON TO
   the sheet's own north-east-to-south-west diagonal, on the glass, as the
   camera has it — so the gesture runs along the paper and not along the
   screen, and it foreshortens with the sheet at any tilt or bearing. Three
   quarters of the diagonal is the whole fold. Let go past a third and the
   paper finishes the fold on its own; let go short of it and it falls back
   open. Nothing snaps: the paper is wherever the hand left it, every
   frame, and stops there if the hand stops.

   AND IT IS NOT SIGNPOSTED. There is no cursor change beyond the one the
   corner has had since R23, no hint, no handle: what tells you the corner
   can be picked up is that it is already lifted and it lifts further under
   the hand — the paper answering for itself, which is R23's whole ruling.
   ==================================================================== */
/* R47: the hand can take hold of the corner on every page now. */
var SNAP = 0.34;              /* let go past this and the paper finishes */
var REACH = 0.75;             /* the whole fold is this much of the diagonal */

/* ==== B1: THE CLIP =================================================
   ONE SVG ELEMENT, laid by the same projection as the corner's hand
   above — `Room.onPaperQuad`, in sheet units, so it rides every pan,
   zoom, tilt, bearing and pose with no draw and no rebuild, and takes
   itself away the moment a fold covers the paper it is lying on. Drawn
   here rather than in the engine because a clip is five flat shapes and
   two shadows: a hundred and fifty lines of WebGL to say what an inline
   path says in ten, and it is the one piece of the scene that must read
   as METAL on paper rather than as paper.

   WHERE IT SITS, in sheet units on a 2364 x 1216 sheet: 190 wide, 132
   deep, its left edge at 965, its top 22 units ABOVE the sheet's own edge
   — the barrel laps over the head of the paper the way a board clip
   does. The sheet's centre is 1182, so it is a little left of centre and
   nowhere near the north-east corner. The top margin is empty between the
   left-hand strapline (ends around x 700) and the range note (x 2285), so
   it covers no print.
   ================================================================== */
var CLIP = { x: 965, y: -22, w: 190, h: 132 };
var clipEl = null, clipT = '', clipLaid = 0, clipOpen = null;

/* ==== S-B2: WHICH CLIP ==============================================
   Tommy, review of 2026-09-19: "probably something like brass or a spring
   clip. Think of the era and the style of the rest, and tell me what you
   think works. Maybe we need a few variants."

   The era the rest of the desk is set in is a late-Victorian survey
   office: an Ordnance-style sheet in copperplate on deckled rag, hachures
   and soundings, oak, a green-shaded lamp. Three clips are cut for it and
   `?clip=a|b|c` picks one --

   a  BRASS DRAWING-BOARD CLIP (the default). A flat brass plate with a
      hinged tongue over it, two screws through the tongue and a steel
      spring showing at the knuckle, all of it aged rather than polished:
      the thing that would already have been screwed to the board.
   b  BLACK-JAPANNED BULLDOG, its two wire handles folded back flat over
      the spine the way they lie when the clip is shut. Later, cheaper,
      office rather than drawing office -- and the only one of the three
      that is BLACK, which is a strong mark to put on a cream sheet.
   c  NICKEL SPRING CLIP -- B1's clip, re-metalled off graphite: cool,
      plain, and the quietest of the three.

   A switch must have a default and the default is a. The whole of the
   difference is the markup below and the fills in styles.css; the
   mechanics -- shut at home, sprung open on the first pan, pressed home
   by a finger, a millimetre under the hand -- belong to the jaw and the
   spring groups and are the same for all three.
   ================================================================== */
var CLIPV = (function () {
  var m = /[?&]clip=([abc])/.exec(location.search);
  return m ? m[1] : 'a';
})();

/* ---- S3: THE CLIP, DRAWN AS A THING AND NOT AS A DIAGRAM ----------
   Tommy, review 3: "the brass clip is fine but why is the model of it
   hideous?" It was hideous because it was a stack of rounded rectangles
   with a gleam painted on: no anatomy. What is drawn now is the object
   an Edwardian drawing office actually screwed to a board -- a flat
   BASE PLATE with two countersunk screws, a rolled HINGE KNUCKLE in
   three barrel segments along its top edge, and a sprung TONGUE hinged
   on that knuckle whose free end is turned up in a LIP a finger can get
   under. The tongue's face is domed ACROSS its width, so the light on it
   is a gradient in x with one highlight running lengthwise; a hairline
   of darker brass sits where the tongue's foot meets the plate.

   THE SHADOW IS ONE BLUR and the only filter in the file. Every other
   part is one linear gradient, its stops classed so the three metals are
   the three blocks of stop-colour in styles.css and nothing else.

   THE MECHANICS ARE UNTOUCHED: `.sc-jaw` is still the moving leaf and
   still hinges as a scaleY about the knuckle's axis, which is what a
   hinge seen from above IS. What changed is that the leaf is now only
   the tongue and its lip -- the plate, the screws and the knuckle stay
   where they are screwed, which is the whole of why the old one read as
   a lid and not as a clip. */
var CLIP_DEFS =
  '<defs>' +
    '<filter id="sc-soft" x="-25%" y="-25%" width="160%" height="170%">' +
      '<feGaussianBlur stdDeviation="3.4"></feGaussianBlur>' +
    '</filter>' +
    /* the plate: light off its top radius, down through the metal */
    '<linearGradient id="sc-gp" x1="0" y1="0" x2="0" y2="1">' +
      '<stop class="sc-s-lit" offset="0"></stop>' +
      '<stop class="sc-s-mid" offset="0.45"></stop>' +
      '<stop class="sc-s-dim" offset="1"></stop>' +
    '</linearGradient>' +
    /* the tongue is DOMED ACROSS ITS WIDTH, so its gradient runs in x */
    '<linearGradient id="sc-gt" x1="0" y1="0" x2="1" y2="0">' +
      '<stop class="sc-s-dim" offset="0"></stop>' +
      '<stop class="sc-s-lit" offset="0.3"></stop>' +
      '<stop class="sc-s-mid" offset="0.62"></stop>' +
      '<stop class="sc-s-dim" offset="1"></stop>' +
    '</linearGradient>' +
    /* a barrel segment is a cylinder: dark, bright band, dark */
    '<linearGradient id="sc-gk" x1="0" y1="0" x2="0" y2="1">' +
      '<stop class="sc-s-dim" offset="0"></stop>' +
      '<stop class="sc-s-lit" offset="0.34"></stop>' +
      '<stop class="sc-s-mid" offset="0.7"></stop>' +
      '<stop class="sc-s-dim" offset="1"></stop>' +
    '</linearGradient>' +
    '<linearGradient id="sc-gs" x1="0" y1="0" x2="0.4" y2="1">' +
      '<stop class="sc-s-lit" offset="0"></stop>' +
      '<stop class="sc-s-dim" offset="1"></stop>' +
    '</linearGradient>' +
  '</defs>';

/* the contact shadow: ONE soft rectangle, offset away from the lamp
   (--lamp-x/--lamp-y put the lamp up and to the left), so the clip sits
   on the paper rather than floating over it */
var CLIP_SHADE =
  '<g class="sc-shade" filter="url(#sc-soft)">' +
    '<rect x="16" y="30" width="170" height="86" rx="13"></rect>' +
  '</g>';

/* the two countersunk screws, which are the same screws in all three
   metals -- a seat, a dished head and a slot cut across it */
function clipScrews(cy) {
  var out = '';
  [25, 165].forEach(function (cx) {
    out += '<g class="sc-screw">' +
      '<circle class="sc-screw-seat" cx="' + cx + '" cy="' + cy + '" r="7.4"></circle>' +
      '<circle class="sc-screw-head" cx="' + cx + '" cy="' + cy + '" r="5.4"></circle>' +
      '<rect class="sc-screw-slot" x="' + (cx - 4.6) + '" y="' + (cy - 0.9) +
            '" width="9.2" height="1.8" rx="0.9"></rect>' +
    '</g>';
  });
  return out;
}

/* the rolled knuckle: three barrel segments on one pin, the gaps between
   them showing the pin itself, which is how a rolled hinge is made */
function clipKnuckle() {
  var out = '<line class="sc-pin" x1="14" y1="25" x2="176" y2="25"></line>';
  [[26, 40], [74, 42], [124, 40]].forEach(function (seg) {
    out += '<rect class="sc-barrel" x="' + seg[0] + '" y="16" width="' + seg[1] +
           '" height="18" rx="9"></rect>' +
           '<rect class="sc-barrel-lit" x="' + (seg[0] + 4) + '" y="19.4" width="' +
           (seg[1] - 8) + '" height="2.6" rx="1.3"></rect>';
  });
  return out;
}

function clipBody(v) {
  if (v === 'b') {
    /* b -- BLACK-JAPANNED BULLDOG. The body is a wedge seen from above
       because a bulldog's jaw is a wedge, the spine is one rolled barrel
       rather than three, and the two drawn-wire handles lie FOLDED BACK
       over the spine the way they lie when the clip is shut. */
    return '<g class="sc-base">' +
        '<path class="sc-plate" d="M18 24h154a10 10 0 0 1 10 10l-8 62a9 9 0 0 1-9 8H25a9 9 0 0 1-9-8L8 34a10 10 0 0 1 10-10z"></path>' +
        '<rect class="sc-barrel" x="10" y="14" width="170" height="20" rx="10"></rect>' +
        '<rect class="sc-barrel-lit" x="20" y="17.6" width="150" height="3" rx="1.5"></rect>' +
      '</g>' +
      '<g class="sc-jaw">' +
        '<path class="sc-tongue" d="M44 26h102a8 8 0 0 1 8 8l-5 56a7 7 0 0 1-7 6H48a7 7 0 0 1-7-6l-5-56a8 8 0 0 1 8-8z"></path>' +
        '<rect class="sc-gleam" x="60" y="32" width="5.5" height="58" rx="2.75"></rect>' +
        '<path class="sc-hair" d="M39 94h112"></path>' +
        '<path class="sc-lip" d="M38 94h114a5 5 0 0 1 5 5v7a5 5 0 0 1-5 5H38a5 5 0 0 1-5-5v-7a5 5 0 0 1 5-5z"></path>' +
        '<rect class="sc-lip-lit" x="41" y="96.4" width="108" height="2.4" rx="1.2"></rect>' +
      '</g>' +
      '<g class="sc-wires">' +
        '<path class="sc-wire" d="M56 24C50 6 66 -2 92 -2"></path>' +
        '<path class="sc-wire" d="M134 24c6 -18 -10 -26 -36 -26"></path>' +
        '<circle class="sc-wire-end" cx="56" cy="24" r="3.4"></circle>' +
        '<circle class="sc-wire-end" cx="134" cy="24" r="3.4"></circle>' +
      '</g>';
  }
  /* a (brass) and c (nickel) are the SAME OBJECT in two metals -- a board
     clip is a board clip -- so the geometry is written once and the whole
     of the difference is the stop-colours. c gets no screw dish, because
     a nickel spring clip of this kind is riveted and not screwed. */
  return '<g class="sc-base">' +
      '<rect class="sc-plate" x="10" y="20" width="170" height="86" rx="11"></rect>' +
      '<rect class="sc-plate-lit" x="17" y="25" width="156" height="3.4" rx="1.7"></rect>' +
      clipScrews(86) +
      clipKnuckle() +
    '</g>' +
    '<g class="sc-jaw">' +
      '<path class="sc-tongue" d="M48 24h94a9 9 0 0 1 9 9v57a6 6 0 0 1-6 6H45a6 6 0 0 1-6-6V33a9 9 0 0 1 9-9z"></path>' +
      /* the one highlight, running the tongue\'s length down the crown of
         the dome -- not a mirror, a single soft line */
      '<rect class="sc-gleam" x="63" y="31" width="5.5" height="60" rx="2.75"></rect>' +
      /* the hairline of darker metal where the leaf meets the plate */
      '<path class="sc-hair" d="M40 94.6h110"></path>' +
      /* and the free end TURNED UP, which is what a finger lifts */
      '<path class="sc-lip" d="M40 94h110a5 5 0 0 1 5 5v7a5 5 0 0 1-5 5H40a5 5 0 0 1-5-5v-7a5 5 0 0 1 5-5z"></path>' +
      '<rect class="sc-lip-lit" x="43" y="96.4" width="104" height="2.4" rx="1.2"></rect>' +
    '</g>';
}

function clipSvg() {
  return '<svg class="sc-svg" viewBox="0 0 190 132" width="190" height="132" ' +
         'aria-hidden="true" focusable="false">' +
    CLIP_DEFS + CLIP_SHADE + clipBody(CLIPV) +
  /* THE HAND ON THE METAL DOES NOT FLICKER. Tommy (2026-09-20): "The
       lighting on the closed clip looks good now, but not when it's open.
       It still jumps about on hover." The jaw, the plate and the wires
       took the pointer each on their own outline, with nothing between
       them, and the jaw LEANS under the hand -- so a pointer near its
       edge fell off it as it leaned, the hover ended, the jaw came back
       up under the pointer, the hover began again: the tongue's lit face
       flickering between two leans is what read as the light jumping.
       One unpainted rectangle over the whole of the metal's own bounds
       (plate, tongue and wires; never the shadow) takes the hover, and
       it does not move. */
    '<rect class="sc-hit" x="8" y="-4" width="174" height="117"></rect>' +
    '</svg>';
}

/* THE CLIP IS OPEN WHENEVER THE MAP IS OFF HOME, which is the one fact
   the corner has always been reading too (`Room.atFit`). Written to the
   DOM only when the answer changes. */
function clipLay() {
  if (!clipEl) return;
  var t = Room.onPaperQuad ? Room.onPaperQuad(CLIP.x, CLIP.y, 1, CLIP.w, CLIP.h) : null;
  if (t === null || t === undefined) {
    if (clipEl.style.visibility !== 'hidden') clipEl.style.visibility = 'hidden';
    return;
  }
  if (clipEl.style.visibility) clipEl.style.visibility = '';
  if (t !== clipT) { clipT = t; clipEl.style.transform = t; }
  if (!clipLaid) { clipLaid = 1; clipEl.classList.add('is-laid'); }
  var open = !(window.Room && Room.atFit && Room.atFit());
  if (open === clipOpen) return;
  clipOpen = open;
  clipEl.classList.toggle('is-open', open);
  if (!open) clipEl.classList.remove('is-shutting');
}

function clipMake(after) {
  if (!after || !after.parentNode) return;
  clipEl = document.createElement('div');
  clipEl.className = 'sheet-clip clip-' + CLIPV;
  clipEl.id = 'sheet-clip';
  clipEl.setAttribute('aria-hidden', 'true');
  clipEl.style.width = CLIP.w + 'px';
  clipEl.style.height = CLIP.h + 'px';
  clipEl.innerHTML = clipSvg();
  after.parentNode.insertBefore(clipEl, after);
  /* A PRESS, and nothing else: the clip is shut by a finger, and a hand
     that travels over it is a hand on its way somewhere. */
  var down = null, moved = 0, shutT = 0;
  clipEl.addEventListener('pointerdown', function (e) {
    if (!clipOpen) return;
    e.stopPropagation(); e.preventDefault();
    down = { x: e.clientX, y: e.clientY }; moved = 0;
    clipEl.classList.add('is-pressed');
  });
  clipEl.addEventListener('pointermove', function (e) {
    if (!down) return;
    moved += Math.abs(e.clientX - down.x) + Math.abs(e.clientY - down.y);
    down = { x: e.clientX, y: e.clientY };
  });
  clipEl.addEventListener('pointerup', function (e) {
    if (!down) return;
    down = null;
    clipEl.classList.remove('is-pressed');
    e.stopPropagation();
    if (moved > 4 || !clipOpen) return;
    /* THE FINGER STAYS ON IT UNTIL THE PAPER IS FLAT. Tommy (2026-09-19):
       "you click it and it goes down like it's clipped, but it immediately
       pops back up. As things actually go flat, it goes down permanently,
       so it bounces up and then back down". The press ended with the
       pointer, so the jaw sprang back open for the half second the map
       took to get home, and only then shut. A clip that has been pressed
       home is held down: `is-shutting` keeps the leaf flat from the
       release until `clipLay` sees the map at the fit and drops `is-open`,
       which is the same picture. If home never comes (a hand pans off
       during the glide) the hold lets go after the glide's own span and
       the leaf springs open again, as it should. */
    clipEl.classList.add('is-shutting');
    if (shutT) clearTimeout(shutT);
    shutT = setTimeout(function () { shutT = 0; clipEl.classList.remove('is-shutting'); }, 1600);
    if (window.RoomObjects && RoomObjects.cornerSettle) RoomObjects.cornerSettle();
    home();
  });
  function off() { down = null; clipEl.classList.remove('is-pressed'); }
  clipEl.addEventListener('pointercancel', off);
  clipEl.addEventListener('pointerleave', off);
  window.addEventListener('blur', off);
  clipEl.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); });
  clipEl.addEventListener('dblclick', function (e) { e.stopPropagation(); });
}

var S = null, el = null, box = null;
var wasLive = null, lastT = '', laid = 0, wasTug = null;

/* A FINGER IS NOT A POINTER, and the corner is sized in the sheet's own
   units, so the coarse-pointer target cannot be a CSS radius the way the
   pin's grip was: the box itself is grown, by JS, from the same media
   query. 1.7 of 70 sheet units is 119, which on the 780 x 1410 canvas is
   about 39 css px across — a thumb's target — and still inside the top
   margin, so it never reaches the plate. */
function grow() {
  try { return window.matchMedia('(pointer: coarse)').matches ? 1 : 1;   /* CG2: was 1.7; the phone's floor is THUMB below */ }
  catch (e) { return 1; }
}

/* ==== CG1 (2026-10-05): A THUMB CAN REACH IT. Tommy, on his iPhone: "The
   corner just doesn't grab on my phone". On its side the phone lays the
   plate across the glass (PH10, LY1), so the sheet's corner lies past the
   glass's top right and the box over it showed a sliver about 55 px wide
   in the very corner of the screen: where an iPhone's display is rounded
   off and its safe area begins, so there was nothing there to touch. On
   the phone the box reaches at least THUMB px each way inside the safe
   area, grown inwards from the edges that cut it. The pull takes the
   hand's travel, not its start, so a press a little in from the paper's
   corner folds it just the same.
   CG2 (2026-10-05): AND NO MORE THAN A THUMB. Tommy: "it doesn't feel like
   you have to actually touch the corner in order to start folding the map,
   which is a way to access an Easter egg, not a normal bit of
   functionality". The sliver he could not grab was mostly GW1's (the
   corner laid off the glass); Chrome keeps the page clear of the rounded
   corners. So the grip is the corner's own (31 x 30 px at 852 x 393) with
   a floor of 36 px, and a coarse pointer no longer grows it (it was 1.7,
   then 1.3: Tommy, at 48 px, "can still [do with it] being smaller"). ==== */
var THUMB = 36, insProbe = null;
function insets() {
  try {
    if (!insProbe) {
      insProbe = document.createElement('div');
      insProbe.style.cssText = 'position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;' +
        'padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
      document.body.appendChild(insProbe);
    }
    var cs = getComputedStyle(insProbe);
    return [parseFloat(cs.paddingTop) || 0, parseFloat(cs.paddingRight) || 0,
            parseFloat(cs.paddingBottom) || 0, parseFloat(cs.paddingLeft) || 0];
  } catch (e) { return [0, 0, 0, 0]; }
}
/* ==== CG3 (2026-10-05): ON THE PHONE THE GRIP IS THE MAP'S OWN CORNER.
   Tommy, of the grip on the paper's corner (past the glass's top right):
   "22 pixels is probably where we want the fold corner to start from. It
   needs to be something you actually can get your finger onto, but it
   should be really small and slightly challenging". So on the phone the
   grip is the top right of the glass down to the top right of the map's
   drawn frame (the neat rule, field.js sheetBox: the sheet's width less
   its left margin), kept partly inside the safe area. The pull takes the hand's travel, so the
   fold that follows is the paper's, as from its own corner. ==== */
var GRIP_SLACK = 0;
function mapCorner() {
  var F = S.fld, C = window.RoomField && RoomField.C;
  if (!C || !F || !F.sheet || !window.Room || !Room.paperPoint) return null;
  var m = C.px - C.gap - C.band - 9, top = C.py - C.gap - C.band - 9;   /* the neat rule's outer corner */
  return Room.paperPoint(F.sheet.w - m, top);
}
function reach(h) {
  if (!S || !S.layout || !/(^|\s)phone(\s|$)/.test(document.documentElement.className)) return h;
  var cw = S.layout.cw, ch = S.layout.ch;
  var mc = mapCorner();
  /* TB2: a tablet laid as the desk (the whole sheet on the glass) takes
     the paper's own corner, as a desk does, with the thumb's floor below */
  var tdesk = /(^|\s)tablet(\s|$)/.test(document.documentElement.className) && (S.layout.wide || 0) > 0.5;
  if (mc && mc[0] > 0 && mc[1] < ch && !tdesk) {
    /* from the glass's top right to the map's corner and no further: Tommy,
       "The box should be all the way from the top right down to ... the top
       right-hand corner of the map axes. And then we'll expand from there
       based on whether it's too big or too small for someone's thumb".
       GRIP_SLACK widens it past the corner, down and left, if thumbs miss. */
    var gx0 = mc[0] - GRIP_SLACK, gy1 = mc[1] + GRIP_SLACK, ni = insets();
    /* Safari lays the page into the rounded corners, where no finger
       reaches: a corner in the inset band keeps 24 px inside the safe area */
    if (ni[1] > 0 && gx0 > cw - ni[1] - 24) gx0 = cw - ni[1] - 24;
    if (ni[0] > 0 && gy1 < ni[0] + 24) gy1 = ni[0] + 24;
    return { x: gx0, y: 0, w: cw - gx0, h: gy1 };
  }
  if (h.x >= cw || h.y >= ch || h.x + h.w <= 0 || h.y + h.h <= 0) return h;   /* wholly off the glass: not ours to bring on */
  var n = insets(), x0 = n[3], y0 = n[0], x1 = cw - n[1], y1 = ch - n[2];
  /* the safe area's part of it, which may be none (a box wholly in the inset band) */
  var sx1 = Math.min(h.x + h.w, x1), sy0 = Math.max(h.y, y0), sx0 = Math.max(h.x, x0), sy1 = Math.min(h.y + h.h, y1);
  if (sx1 - sx0 >= THUMB && sy1 - sy0 >= THUMB) return h;
  var nx0 = Math.min(h.x, sx1 - THUMB), nx1 = Math.max(h.x + h.w, sx1);
  var ny0 = Math.min(h.y, sy0), ny1 = Math.max(h.y + h.h, sy0 + THUMB);
  return { x: nx0, y: ny0, w: nx1 - nx0, h: ny1 - ny0 };
}

/* ---- laid on the paper by the projection E9 wrote, R21 published ----
   The sheet is a plane: three projected points are a complete affine, so
   one matrix puts the box exactly over the corner at any pose. */
function lay(st) {
  S = st;
  clipLay();
  if (!el) return;
  if (!box) {
    if (!window.RoomObjects || !RoomObjects.cornerBox) return;
    box = RoomObjects.cornerBox(grow());
    el.style.width = box.s.toFixed(2) + 'px';
    el.style.height = box.s.toFixed(2) + 'px';
  }
  /* First ask the paper projection whether this panel is still exposed.
     The fold can cover it, and no screen-space box may reach through the
     folded packet. */
  var t = Room.onPaperQuad(box.x, box.y, 1, box.s, box.s);
  if (t === null) { if (el.style.visibility !== 'hidden') el.style.visibility = 'hidden'; }
  else {
    if (el.style.visibility) el.style.visibility = '';
    /* R82: the drawn curl can project east of the sheet's flat corner.
       Lay the undrawn hand over the union of the original fold grip and
       the curl's real projected mesh, rather than over its abandoned flat
       plane. This remains a small local box; it does not intercept the
       rest of the canvas or any DOM laid over the paper. */
    var hit = RoomObjects.cornerHit ? RoomObjects.cornerHit(S, grow()) : null;
    if (hit) hit = reach(hit);                       /* CG1 */
    var ht = hit ? 'translate3d(' + hit.x.toFixed(2) + 'px,' + hit.y.toFixed(2) + 'px,0)' : t;
    if (hit) {
      el.style.width = hit.w.toFixed(2) + 'px';
      el.style.height = hit.h.toFixed(2) + 'px';
    }
    if (ht !== lastT) { lastT = ht; el.style.transform = ht; }
    /* AND NOT BEFORE IT HAS BEEN LAID (R14's lesson): an untransformed
       absolute box sits at the window's own origin. It draws nothing, but
       it would take the pointer over the top-left corner of the oak for
       whatever paint falls between `room-ready` and the first draw. */
    if (!laid) { laid = 1; el.classList.add('is-laid'); }
  }
  /* THE HAND IS ON IT ONLY WHILE THERE IS SOMETHING TO UNDO. The class is
     written only when the answer changes, so a pan writes nothing to the
     DOM until it crosses the fit. */
  /* X2: with the fold on, the corner is live even at the fit — a sheet
     lying perfectly flat is exactly the sheet you would fold, and the
     hand has to be able to take hold of it. Off the fit it is R23's. */
  /* B2: AND WHETHER THE CORNER HAS BEEN TUGGED. The class carries one
     thing only — the grab cursor — because the lift itself is the scene's
     (objects.js), not the element's. */
  var tug = !!(window.RoomObjects && RoomObjects.cornerTugged && RoomObjects.cornerTugged());
  if (tug !== wasTug) { wasTug = tug; el.classList.toggle('is-tug', tug); }
  if (wasLive) return;
  wasLive = true;
  el.classList.add('is-live');
}

function home() {
  /* B2: the tug belongs to the widest view and the map is leaving it. */
  if (window.RoomObjects && RoomObjects.cornerTug) RoomObjects.cornerTug(false);
  if (window.Room && Room.home) Room.home(true);
}

/* ---- THE PUSH (v32 wireInstrument :5966, R14's shape) --------------
   A press that does not travel is a push; anything else is a hand that
   changed its mind. Inert while the sheet is at the fit. */
function wire(g) {
  var down = null, moved = 0, pull = null, pid = -1;
  function inert() { return !g.classList.contains('is-live'); }
  /* the sheet's diagonal on the glass, taken once at the press: the paper
     does not move under the hand while the hand is on it (the map is
     detached the moment there is a fold in the sheet), so one reading is
     the whole gesture and a pointermove costs two multiplies */
  function grip(e) {
    if (!window.Room || !Room.paperPoint || !window.RoomObjects) return null;
    var fg = RoomObjects.foldGeom ? RoomObjects.foldGeom() : null;
    if (!fg) return null;
    var ne = Room.paperPoint(fg.w, 0), sw = Room.paperPoint(0, fg.h);
    if (!ne || !sw) return null;
    var dx = sw[0] - ne[0], dy = sw[1] - ne[1], m = Math.hypot(dx, dy);
    if (m < 40) return null;
    return { x: e.clientX, y: e.clientY, ux: dx / m, uy: dy / m,
             run: m * REACH, t0: RoomObjects.foldT || 0 };
  }
  g.addEventListener('pointerenter', function () {
    if (!inert() && window.RoomObjects) RoomObjects.cornerHover(true);
  });
  g.addEventListener('pointerleave', function () {
    if (window.RoomObjects) RoomObjects.cornerHover(false);
  });
  g.addEventListener('pointerdown', function (e) {
    if (inert()) return;
    e.stopPropagation(); e.preventDefault();
    down = { x: e.clientX, y: e.clientY }; moved = 0; pid = e.pointerId;
    pull = grip(e);
    try { g.setPointerCapture(e.pointerId); } catch (err) { /* fine */ }
  });
  g.addEventListener('pointermove', function (e) {
    if (!down) return;
    moved += Math.abs(e.clientX - down.x) + Math.abs(e.clientY - down.y);
    down = { x: e.clientX, y: e.clientY };
    /* X2: PAST THE PUSH, THE CORNER IS BEING PULLED. Four pixels is
       R14's own line between a push and a hand that changed its mind;
       past it, with the fold on, the hand is folding the sheet. */
    if (!pull || moved <= 4 || !window.Room || !Room.foldAt) return;
    RoomObjects.foldGrab = 1;
    var t = pull.t0 + ((e.clientX - pull.x) * pull.ux + (e.clientY - pull.y) * pull.uy) / pull.run;
    Room.foldAt(t);
  });
  g.addEventListener('pointerup', function (e) {
    if (!down) return;
    down = null;
    e.stopPropagation();
    if (pull && moved > 4 && window.RoomObjects && RoomObjects.foldGrab) {
      /* THE HAND LETS GO. Past a third of the way the paper finishes the
         fold and the reader stands up; short of it, it falls back open. */
      RoomObjects.foldGrab = 0;
      var t = RoomObjects.foldT || 0;
      pull = null;
      Room.foldTo(t >= SNAP ? 1 : 0, 1100);
      return;
    }
    pull = null;
    /* ==== B1: AND A PUSH ON THE CORNER IS NOTHING NOW ================
       R23's push took the sheet home from here. The clip at the head of
       the paper is the way home as of B1, and two ways home on one sheet
       is one too many — worse, it made the corner answer to both a
       fingertip and a hand, which is exactly the ambiguity the fold does
       not need. What is left on this corner is the PULL above. ==== */
  });
  /* ==== B4: AND EVERY OTHER WAY THE HAND CAN LEAVE THE CORNER ========
     B3 shut this for the glass; the corner has the same four holes and a
     worse consequence. The press above sets `down` and `pull`, and past
     four pixels the pull sets `RoomObjects.foldGrab` — and until now only
     an up or a cancel arriving AT THIS ELEMENT cleared any of it. The
     capture is asked for inside a try/catch because it can simply not be
     given (a synthetic or inactive pointer has none), and it can be taken
     away again afterwards: another element takes the pointer, the button
     is let go outside the window, the window loses focus mid-pull, the
     tab is hidden. In each of those the up lands somewhere else or never
     happens at all.

     MEASURED before this card (room-rev10/b4-specs/before-175.json): all
     four leave the fold stuck at 0.2033 with `foldGrab` set — and
     `foldGrab` is exactly what `mapLive()` reads (engine.js :4081), so
     laying the sheet flat again does NOT bring the map back: the drag
     after it moved the view 0 units and the engine counted two refusals
     (`dragOff` 2). Dead until the page is reloaded, from the corner as
     surely as from the glass. The stale `down` is the second leg, and it
     is the `pts` leak in this file's own terms: `pointermove` gates on
     `down` and never looks at `e.buttons`, so a pointer merely PASSING
     OVER the corner with no button held was still taken for the hand and
     the paper answered it — 0.2033 -> 0.0331, the sheet folding itself
     under a hand that is not touching it.

     `letGo` is the RELEASE HALF of the up above and nothing else: a lost
     pointer is not R23's push, so it never takes the sheet home and never
     calls `cornerSettle`. The paper is put down where a finger lifting at
     that moment would have put it — past a third it finishes, short of it
     it falls back open, which is the line the cancel has always taken.
     Each of these does something only while this hand is still on the
     corner, so the `lostpointercapture` that follows every ordinary
     release finds nothing left to do, and none of them can run per
     frame. ==== */
  function letGo(id) {
    if (id !== undefined && id !== pid) return;
    if (!down && !pull) return;
    down = null; moved = 0;
    if (pull && window.RoomObjects && RoomObjects.foldGrab) {
      RoomObjects.foldGrab = 0;
      Room.foldTo((RoomObjects.foldT || 0) >= SNAP ? 1 : 0, 1100);
    }
    pull = null;
  }
  g.addEventListener('pointercancel', function (e) { letGo(e.pointerId); });
  g.addEventListener('lostpointercapture', function (e) { letGo(e.pointerId); });
  /* The up that never came back here. This pair sits on the window in the
     BUBBLE phase, below the corner's own listeners, so a release that does
     reach the corner is still answered by the handler above first — with
     its push, its settle and its stopPropagation — and this finds nothing
     left when it arrives. */
  window.addEventListener('pointerup', function (e) { letGo(e.pointerId); });
  window.addEventListener('pointercancel', function (e) { letGo(e.pointerId); });
  /* A hand that is now somewhere else entirely: the button will be let go
     in another window, or on another tab, and no pointer event of any kind
     is coming back. */
  window.addEventListener('blur', function () { letGo(); });
  document.addEventListener('visibilitychange', function () { if (document.hidden) letGo(); });
  g.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); });
  g.addEventListener('dblclick', function (e) { e.stopPropagation(); });
}

function init(st) {
  S = st;
  el = document.getElementById('sheet-corner');
  if (el) { wire(el); clipMake(el); }

  /* ---- A DOUBLE-CLICK ON THE DESK, NOT ON THE PLATE (R14) ---------
     The desk is everything outside the window in the paper: the margins,
     the cartouche, the boards either side. A double-click on the PLATE is
     not a reset — the ground there answers a click by pinning the day
     (E6), and a reader tapping twice at a day means the day. */
  var host = document.getElementById('canvas');
  if (host) host.addEventListener('dblclick', function (e) {
    var P = S.portalScr || S.portal;              /* MP2: on the glass */
    if (!P) return;
    var r = host.getBoundingClientRect();
    var x = e.clientX - r.left, y = e.clientY - r.top;
    if (x >= P.l && x <= P.l + P.w && y >= P.t && y <= P.t + P.h) return;
    home();
  });

  /* ---- R AND 0 ARE STILL THE KEYS (Esc is engine.js's own chain) --- */
  window.addEventListener('keydown', function (e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    var t = e.target;
    if (t && t.matches && t.matches('input,textarea,[contenteditable]')) return;
    /* X2: and not from the room, and not with the sheet folded — the keys
       are the reader's hand on the map like everything else, and in the
       room the reader's hand is not on the map. Esc is the way out of the
       room and it always was (engine.js's own chain). */
    if (window.Room && Room.mapLive && !Room.mapLive()) return;
    if (e.key === 'r' || e.key === 'R' || e.key === '0') { e.preventDefault(); home(); }
  });
}

/* The name stays RoomPin: engine.js calls `RoomPin.lay(S)` every frame and
   there has been no pin in this engine for a long time either way. */
window.RoomPin = { init: init, lay: lay, home: home };
})();
